import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import paper from 'paper';

import { TextAnnotation } from '@paragon/contract-library';

import { editAnnotation } from '../../actions/annotations';
import { showEditAnnotationPrompt } from '../../actions/popup';
import { mockAnnotationBoundingBox } from '../../modules/annotations/annotationUtilities';
import { pointsToPixels } from '../../modules/annotations/paperItemTranslator';
import { getAnnotationsForCurrentPageWithSelectionData } from '../../selectors/annotations';
import { getSelectedPageImageInfo } from '../../selectors/documents';
import { setTextAnnotationBeingEditedInlineGuid } from '../../store/annotations';
import { DEFAULT_FONT } from '../dialogs/annotation-editing/TextOptions';
import { useAppDispatch, useAppSelector } from '../hooks';

const TEXTAREA_PADDING_X = 10;
const TEXTAREA_MINIMUM_SIZE_X = 100;
const TEXTAREA_MINIMUM_SIZE_Y = 40;

interface EditTextAnnotationInlineProps {
  readonly textAnnotationBeingEditedInlineGuid: string;
  readonly topLeftInCanvasCoordinates: paper.Point;
}

export function EditTextAnnotationInline(props: EditTextAnnotationInlineProps) {
  const { textAnnotationBeingEditedInlineGuid, topLeftInCanvasCoordinates } = props;

  const dispatch = useAppDispatch();
  const pageImageInfo = useAppSelector(getSelectedPageImageInfo);
  const currentPageAnnotations = useAppSelector(getAnnotationsForCurrentPageWithSelectionData);

  const zoom = paper.view.zoom; // zooming is disabled during inline editing, so this is stable

  const textAnnotationBeingEdited = currentPageAnnotations.find(
    (annotation) => annotation.guid === textAnnotationBeingEditedInlineGuid,
  ) as TextAnnotation;

  const [newText, setNewText] = useState<string | null>(null);

  // Focus on open
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!textAnnotationBeingEdited || textAreaRef.current === null) return;
    textAreaRef.current.selectionStart = textAreaRef.current.value.length;
    textAreaRef.current.focus();
  }, [textAnnotationBeingEdited, textAreaRef]);

  if (pageImageInfo === null) return null;

  const boundingBoxForEditedText = mockAnnotationBoundingBox(
    {
      ...textAnnotationBeingEdited,
      contents: newText ?? textAnnotationBeingEdited.contents,
    },
    pageImageInfo,
  );
  const width = Math.abs(boundingBoxForEditedText.topRight.x - boundingBoxForEditedText.topLeft.x);
  const height = Math.abs(boundingBoxForEditedText.topLeft.y - boundingBoxForEditedText.bottomLeft.y);

  function updateTextAndLeaveInlineEditing() {
    if (newText === null || textAnnotationBeingEdited === null) return;

    dispatch(
      editAnnotation(textAnnotationBeingEdited.guid, {
        contents: newText,
        rotationAngle: textAnnotationBeingEdited.rotation,
        color: textAnnotationBeingEdited.color,
        fontSize: textAnnotationBeingEdited.fontSize,
        fontFamily: textAnnotationBeingEdited.fontFamily ?? DEFAULT_FONT,
        isItalic: textAnnotationBeingEdited.isItalic ?? false,
        isBold: textAnnotationBeingEdited.isBold ?? false,
        isUnderlined: textAnnotationBeingEdited.isUnderlined ?? false,
        sharedWith: textAnnotationBeingEdited.sharedWith,
        translationArea: textAnnotationBeingEdited.translationArea ?? undefined,
      }),
    );
    dispatch(setTextAnnotationBeingEditedInlineGuid(null));
    setNewText(null);
  }

  return topLeftInCanvasCoordinates && pageImageInfo ? (
    <Box>
      <Box
        sx={{
          position: 'absolute',
          top: topLeftInCanvasCoordinates.y - 25,
          left: topLeftInCanvasCoordinates.x,
          height: 25,
          backgroundColor: 'background.default',
          px: 1,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Typography>Shift+Enter for new line | Ctrl+E to continue in edit dialog</Typography>
      </Box>
      <textarea
        ref={textAreaRef}
        value={newText ?? textAnnotationBeingEdited.contents}
        style={{
          position: 'absolute',
          top: topLeftInCanvasCoordinates.y,
          left: topLeftInCanvasCoordinates.x,
          width: Math.max(
            pointsToPixels(width, pageImageInfo.dpi) * zoom + TEXTAREA_PADDING_X,
            TEXTAREA_MINIMUM_SIZE_X,
          ),
          height: Math.max(pointsToPixels(height, pageImageInfo.dpi) * zoom, TEXTAREA_MINIMUM_SIZE_Y),
          fontSize: `${pointsToPixels(textAnnotationBeingEdited.fontSize, pageImageInfo.dpi) * zoom}px`,
          fontFamily: textAnnotationBeingEdited.fontFamily ?? undefined,
          color: textAnnotationBeingEdited.color,
          lineHeight: `${(textAnnotationBeingEdited.leading / textAnnotationBeingEdited.fontSize) * 100}%`,
          padding: 0,
          resize: 'none',
          // Sometimes it tries to show scrollbars which messes up line breaks. Since the box grows with the text,
          // scrollbars are not needed.
          overflow: 'hidden',
        }}
        onChange={(event) => void setNewText(event.target.value)}
        onBlur={updateTextAndLeaveInlineEditing}
        onKeyDown={(event) => {
          if (!event.shiftKey && event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            updateTextAndLeaveInlineEditing();
          } else if (event.key === 'Escape') {
            event.stopPropagation();
            event.preventDefault();
            updateTextAndLeaveInlineEditing();
          } else if (event.ctrlKey && event.key === 'e') {
            event.stopPropagation();
            event.preventDefault();
            dispatch(
              showEditAnnotationPrompt({
                annotationBeingEdited: {
                  ...textAnnotationBeingEdited,
                  contents: newText ?? textAnnotationBeingEdited.contents,
                },
                color: textAnnotationBeingEdited.color,
                annotationType: textAnnotationBeingEdited.type,
                annotationBoundingBox: textAnnotationBeingEdited.boundingBox,
                rotation: textAnnotationBeingEdited.rotation,
                fontSize: textAnnotationBeingEdited.fontSize,
                textContent: newText ?? textAnnotationBeingEdited.contents,
              }),
            );
            dispatch(setTextAnnotationBeingEditedInlineGuid(null));
            setNewText(null);
          }
        }}
      />
    </Box>
  ) : null;
}
